<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { subDays } from 'date-fns';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import userAuthLogReports from 'dashboard/api/userAuthLogReports';
import ReportHeader from './components/ReportHeader.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();

const toDateString = date => date.toISOString().split('T')[0];

const since = ref(toDateString(subDays(new Date(), 6)));
const until = ref(toDateString(new Date()));
const selectedUserId = ref('');
const selectedEvent = ref('');
const page = ref(1);
const isLoading = ref(false);
const rows = ref([]);
const meta = ref({ current_page: 1, total_count: 0, total_pages: 1 });
const error = ref(false);

const accountId = computed(() => store.getters['getCurrentAccountId']);
const agents = computed(() => store.getters['agents/getAgents']);

const BRT_UTC_OFFSET = 3; // BRT = UTC-3

const toUnix = (dateStr, endOfDay = false) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const h = endOfDay ? 23 : 0;
  const m = endOfDay ? 59 : 0;
  const s = endOfDay ? 59 : 0;
  const ms = endOfDay ? 999 : 0;
  return Math.floor(Date.UTC(year, month - 1, day, h + BRT_UTC_OFFSET, m, s, ms) / 1000).toString();
};

const buildParams = () => ({
  since: toUnix(since.value),
  until: toUnix(until.value, true),
  user_id: selectedUserId.value || undefined,
  event: selectedEvent.value || undefined,
  page: page.value,
  timezone_offset: -BRT_UTC_OFFSET,
});

const fetchReports = async () => {
  error.value = false;
  isLoading.value = true;
  try {
    const { data } = await userAuthLogReports.getReports(accountId.value, buildParams());
    rows.value = data.data;
    meta.value = data.meta;
  } catch {
    error.value = true;
  } finally {
    isLoading.value = false;
  }
};

const onFilter = () => {
  page.value = 1;
  fetchReports();
};

const prevPage = () => {
  if (page.value <= 1) return;
  page.value -= 1;
  fetchReports();
};

const nextPage = () => {
  if (page.value >= meta.value.total_pages) return;
  page.value += 1;
  fetchReports();
};

const downloadCSV = async () => {
  const { data: blob } = await userAuthLogReports.downloadCSV(accountId.value, buildParams());
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'relatorio_login_logout.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const exportExcel = () => {
  const headers = [
    t('USER_AUTH_LOG_REPORT.TABLE.AGENT'),
    t('USER_AUTH_LOG_REPORT.TABLE.EMAIL'),
    t('USER_AUTH_LOG_REPORT.TABLE.EVENT'),
    t('USER_AUTH_LOG_REPORT.TABLE.IP'),
    t('USER_AUTH_LOG_REPORT.TABLE.DATE'),
  ];
  const wsData = [
    headers,
    ...rows.value.map(r => [
      r.user_name,
      r.user_email,
      t(`USER_AUTH_LOG_REPORT.EVENT.${r.event}`),
      r.ip_address || '',
      formatDate(r.created_at),
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Relatório');
  XLSX.writeFile(wb, 'relatorio_login_logout.xlsx');
};

const exportPDF = () => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;

  // Cabeçalho
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text(t('USER_AUTH_LOG_REPORT.HEADER'), margin, 20);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  const period = `${t('USER_AUTH_LOG_REPORT.FILTER.FROM')}: ${since.value}   ${t('USER_AUTH_LOG_REPORT.FILTER.TO')}: ${until.value}`;
  doc.text(period, margin, 27);

  const appliedFilters = [];
  if (selectedUserId.value) {
    const agent = agents.value.find(a => a.id === selectedUserId.value);
    if (agent) appliedFilters.push(`${t('USER_AUTH_LOG_REPORT.FILTER.AGENT')}: ${agent.name}`);
  }
  if (selectedEvent.value) {
    appliedFilters.push(`${t('USER_AUTH_LOG_REPORT.FILTER.EVENT_TYPE')}: ${t(`USER_AUTH_LOG_REPORT.EVENT.${selectedEvent.value}`)}`);
  }
  if (appliedFilters.length) {
    doc.text(appliedFilters.join('   '), margin, 33);
  }

  const tableStartY = appliedFilters.length ? 38 : 33;

  autoTable(doc, {
    startY: tableStartY,
    head: [[
      t('USER_AUTH_LOG_REPORT.TABLE.AGENT'),
      t('USER_AUTH_LOG_REPORT.TABLE.EMAIL'),
      t('USER_AUTH_LOG_REPORT.TABLE.EVENT'),
      t('USER_AUTH_LOG_REPORT.TABLE.IP'),
      t('USER_AUTH_LOG_REPORT.TABLE.DATE'),
    ]],
    body: rows.value.map(r => [
      r.user_name,
      r.user_email,
      t(`USER_AUTH_LOG_REPORT.EVENT.${r.event}`),
      r.ip_address || '—',
      formatDate(r.created_at),
    ]),
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 55 },
      2: { cellWidth: 20 },
      3: { cellWidth: 32 },
      4: { cellWidth: 'auto' },
    },
    tableWidth: pageW - margin * 2,
    margin: { left: margin, right: margin },
    repeatHead: true,
    didDrawPage: data => {
      const pageCount = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `${t('USER_AUTH_LOG_REPORT.PAGINATION.SUMMARY', { current: currentPage, total: pageCount, count: meta.value.total_count })}`,
        margin,
        doc.internal.pageSize.getHeight() - 8
      );
    },
  });

  doc.save('relatorio_login_logout.pdf');
};

const EVENT_CLASSES = {
  login: 'bg-green-100 text-green-800',
  logout: 'bg-red-100 text-red-700',
};

const eventClass = val => EVENT_CLASSES[val] ?? 'bg-gray-100 text-gray-600';

const formatDate = iso => {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(iso));
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchReports();
});
</script>

<template>
  <div>
    <ReportHeader
      :header-title="$t('USER_AUTH_LOG_REPORT.HEADER')"
      :header-description="$t('USER_AUTH_LOG_REPORT.DESCRIPTION')"
      class="print:hidden"
    >
      <V4Button
        :label="$t('USER_AUTH_LOG_REPORT.RELOAD')"
        icon="i-ph-arrows-clockwise"
        size="sm"
        variant="secondary"
        :disabled="isLoading"
        @click="fetchReports"
      />
      <V4Button
        :label="$t('USER_AUTH_LOG_REPORT.DOWNLOAD_CSV')"
        icon="i-ph-download-simple"
        size="sm"
        :disabled="isLoading || rows.length === 0"
        @click="downloadCSV"
      />
      <V4Button
        :label="$t('USER_AUTH_LOG_REPORT.DOWNLOAD_EXCEL')"
        icon="i-ph-microsoft-excel-logo"
        size="sm"
        variant="secondary"
        :disabled="isLoading || rows.length === 0"
        @click="exportExcel"
      />
      <V4Button
        :label="$t('USER_AUTH_LOG_REPORT.DOWNLOAD_PDF')"
        icon="i-ph-file-pdf"
        size="sm"
        variant="secondary"
        :disabled="isLoading || rows.length === 0"
        @click="exportPDF"
      />
    </ReportHeader>

    <div class="flex flex-wrap gap-4 mb-6 print:hidden">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('USER_AUTH_LOG_REPORT.FILTER.FROM') }}
        </label>
        <input
          v-model="since"
          type="date"
          class="px-3 py-2 border border-n-weak rounded-lg text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('USER_AUTH_LOG_REPORT.FILTER.TO') }}
        </label>
        <input
          v-model="until"
          type="date"
          class="px-3 py-2 border border-n-weak rounded-lg text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('USER_AUTH_LOG_REPORT.FILTER.AGENT') }}
        </label>
        <select
          v-model="selectedUserId"
          class="px-3 py-2 border border-n-weak rounded-lg text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500 min-w-[180px]"
          @change="onFilter"
        >
          <option value="">{{ $t('USER_AUTH_LOG_REPORT.FILTER.ALL_AGENTS') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('USER_AUTH_LOG_REPORT.FILTER.EVENT_TYPE') }}
        </label>
        <select
          v-model="selectedEvent"
          class="px-3 py-2 border border-n-weak rounded-lg text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500 min-w-[140px]"
          @change="onFilter"
        >
          <option value="">{{ $t('USER_AUTH_LOG_REPORT.FILTER.ALL_EVENTS') }}</option>
          <option value="login">{{ $t('USER_AUTH_LOG_REPORT.FILTER.LOGIN_ONLY') }}</option>
          <option value="logout">{{ $t('USER_AUTH_LOG_REPORT.FILTER.LOGOUT_ONLY') }}</option>
        </select>
      </div>
      <div class="flex items-end">
        <V4Button
          :label="$t('USER_AUTH_LOG_REPORT.FILTER.APPLY')"
          icon="i-ph-funnel"
          size="sm"
          :disabled="isLoading"
          @click="onFilter"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-12 print:hidden">
      <span class="text-sm text-n-slate-11">{{ $t('USER_AUTH_LOG_REPORT.LOADING') }}</span>
    </div>

    <div v-else-if="error" class="text-center py-12 print:hidden">
      <span class="text-sm text-red-500">{{ $t('USER_AUTH_LOG_REPORT.ERROR') }}</span>
    </div>

    <template v-else>
      <div v-if="rows.length === 0" class="text-center py-12 print:hidden">
        <span class="text-sm text-n-slate-11">{{ $t('USER_AUTH_LOG_REPORT.NO_DATA') }}</span>
      </div>

      <template v-else>
        <div class="hidden print:block mb-4">
          <h1 class="text-xl font-bold">{{ $t('USER_AUTH_LOG_REPORT.HEADER') }}</h1>
          <p class="text-sm text-gray-600">{{ since }} — {{ until }}</p>
        </div>

        <div class="overflow-x-auto rounded-lg border border-n-weak">
          <table class="w-full text-sm">
            <thead class="bg-n-surface-2">
              <tr>
                <th class="px-4 py-3 text-left font-semibold text-n-slate-11">
                  {{ $t('USER_AUTH_LOG_REPORT.TABLE.AGENT') }}
                </th>
                <th class="px-4 py-3 text-left font-semibold text-n-slate-11">
                  {{ $t('USER_AUTH_LOG_REPORT.TABLE.EMAIL') }}
                </th>
                <th class="px-4 py-3 text-left font-semibold text-n-slate-11">
                  {{ $t('USER_AUTH_LOG_REPORT.TABLE.EVENT') }}
                </th>
                <th class="px-4 py-3 text-left font-semibold text-n-slate-11">
                  {{ $t('USER_AUTH_LOG_REPORT.TABLE.IP') }}
                </th>
                <th class="px-4 py-3 text-left font-semibold text-n-slate-11">
                  {{ $t('USER_AUTH_LOG_REPORT.TABLE.DATE') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="row in rows"
                :key="row.id"
                class="hover:bg-n-surface-2 transition-colors"
              >
                <td class="px-4 py-3 font-medium text-n-slate-12">{{ row.user_name }}</td>
                <td class="px-4 py-3 text-n-slate-11">{{ row.user_email }}</td>
                <td class="px-4 py-3">
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      eventClass(row.event),
                    ]"
                  >
                    {{ $t(`USER_AUTH_LOG_REPORT.EVENT.${row.event}`) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-n-slate-11 tabular-nums font-mono text-xs">
                  {{ row.ip_address || '—' }}
                </td>
                <td class="px-4 py-3 text-n-slate-11 tabular-nums">
                  {{ formatDate(row.created_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex items-center justify-between mt-4 print:hidden">
          <span class="text-sm text-n-slate-11">
            {{
              $t('USER_AUTH_LOG_REPORT.PAGINATION.SUMMARY', {
                current: meta.current_page,
                total: meta.total_pages,
                count: meta.total_count,
              })
            }}
          </span>
          <div class="flex gap-2">
            <V4Button
              :label="$t('USER_AUTH_LOG_REPORT.PAGINATION.PREV')"
              icon="i-ph-caret-left"
              size="sm"
              variant="secondary"
              :disabled="meta.current_page <= 1"
              @click="prevPage"
            />
            <V4Button
              :label="$t('USER_AUTH_LOG_REPORT.PAGINATION.NEXT')"
              icon="i-ph-caret-right"
              size="sm"
              variant="secondary"
              :disabled="meta.current_page >= meta.total_pages"
              @click="nextPage"
            />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
